import React, { useState, useEffect } from "react";
import "./navbar.css";
import { exportToGoogleTasks } from "../../services/googleTasksService";
import { activeLuckets, activeLucketsWeek } from "./slice/lucket-model/filterBuckets";
import LucketStepIcon from "./lucket/lucketStepIcon/LucketStepIcon.cmp";
import LucketTimeFrameIcon from "./lucket/LucketTimeFrameIcon";
import LucketCloseOptionsIcon from "./lucket/lucketCloseOptionsIcon";
import LucketRelativePointsIcon from "./lucket/lucketRelativePointsIcon/LucketRelativePointsIcon";
import { AddBucket } from "./AddBucket.cmp";
import { STEP_OPTION, STATUS_OPTION, TIMEFRAME, STATUS_OPTION_HEX_PALLETE, NAV_BACKGROUND_COLOR, LUCKET_DETAILS_BACKGROUND_COLOR } from "./constants";
import { Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from "react-redux";
import { selectStepMenuOpen, selectTimeFrameMenuOpen, selectCloseOptionsMenuOpen, setBucketList, setCalendarTodos, setStepMenuOpen, setTimeFrameMenuOpen, setCloseOptionsMenuOpen, selectCurrentDate, selectBucketViewIsOn, toggleBucketViewIsOn } from "./slice/buckets";

import { getSpreadsheetData } from "./slice/functions";
const NavBar = ({
  showSessionTracker,
  setShowSessionTracker,
  user,
  handleLogout,
  setAddBucketOpen,
  addBucketOpen,
  closeDay,
  step,
  dispatchSetStep,
  timeFrame,
  dispatchSetTimeFrame,
  closeOption,
  dispatchSetCloseOption,
  handlePromptOpenAI,
  buckets,
  showSpreadsheetTable, // Receive state from BucketList
  setShowSpreadsheetTable, // Receive state setter from BucketList
  showCalendar,
  setShowCalendar,
  showBreakManager,
  setShowBreakManager,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [exportingToTasks, setExportingToTasks] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [pointsByColor, setPointsByColor] = useState({
    red: 0,
    yellow: 0,
    green: 0,
    blue: 0,
    purple: 0,
    black: 0,
  });
  const [weekPointsByColor, setWeekPointsByColor] = useState({
    red: 0,
    yellow: 0,
    green: 0,
    blue: 0,
    purple: 0,
    black: 0,
  });
  const stepMenuOpen = useSelector(selectStepMenuOpen);
  const timeFrameMenuOpen = useSelector(selectTimeFrameMenuOpen);
  const closeOptionsMenuOpen = useSelector(selectCloseOptionsMenuOpen);
  const currentDate = useSelector(selectCurrentDate);
  const bucketViewIsOn = useSelector(selectBucketViewIsOn);
  const dispatch = useDispatch();

  const setStepMenuOpenProp = (isOpen) => {
    dispatch(setStepMenuOpen(isOpen));
  }
  const setTimeFrameMenuOpenProp = (isOpen) => {
    dispatch(setTimeFrameMenuOpen(isOpen));
  }
  const setCloseOptionsMenuOpenProp = (isOpen) => {
    dispatch(setCloseOptionsMenuOpen(isOpen));
  }

  const pointsView = (week) => {
    const points = week ? weekPointsByColor : pointsByColor;
    return <div className="points-by-color">
      {Object.keys(points).filter(v => points[v] > 0).map(color => (
        <Tooltip title={`Total ${color.charAt(0).toUpperCase() + color.slice(1)} ${week ? "Week" : "Day"} Action Points`}>
          <div key={color} className={`color-${week ? "circle" : "square"} ${color}`} style={{ backgroundColor: STATUS_OPTION_HEX_PALLETE[color], borderColor: LUCKET_DETAILS_BACKGROUND_COLOR, borderStyle: "Solid", borderWidth: "1px" }}>
            {points[color]}
          </div>
        </Tooltip>
      ))}
      <Tooltip title={`Total${week ? " Week" : ""} Action Points`}>
        <div className={`color-${week ? "circle" : "square"} white`} style={{ backgroundColor: "white", color: "black", borderColor: LUCKET_DETAILS_BACKGROUND_COLOR, borderStyle: "Solid", borderWidth: "1px" }}>
          {totalPoints(week)}
        </div>
      </Tooltip>
    </div>
  }

  useEffect(() => {
    if (buckets) {
      let currentLuckets = buckets.filter(b => currentDate == b.date);

      const points = { red: 0, yellow: 0, green: 0, blue: 0, purple: 0, black: 0 };
      const weekPoints = { red: 0, yellow: 0, green: 0, blue: 0, purple: 0, black: 0 };
      currentLuckets.forEach(bucket => {
        if (bucket.actionStatus && points[bucket.actionStatus] !== undefined) {
          points[bucket.actionStatus] += Number(bucket.points) || 0;
        }
        if (bucket.weekActionStatus && weekPoints[bucket.weekActionStatus] !== undefined) {
          weekPoints[bucket.weekActionStatus] += Number(bucket.weekPoints) || 0;
        }
      });
      setPointsByColor(points);
      setWeekPointsByColor(weekPoints);
    }
  }, [buckets, currentDate]);

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const handleExportToGoogleTasks = async () => {
    try {
      setExportingToTasks(true);
      setExportMessage(null);

      // Get current "Do" buckets based on timeframe
      let bucketsToExport;
      if (timeFrame === TIMEFRAME.Day) {
        bucketsToExport = activeLuckets(buckets, currentDate);
      } else if (timeFrame === TIMEFRAME.Week) {
        bucketsToExport = activeLucketsWeek(buckets, currentDate);
      } else {
        // Default to Day if no specific timeframe
        bucketsToExport = activeLuckets(buckets, currentDate);
      }

      if (bucketsToExport.length === 0) {
        setExportMessage({ type: 'warning', text: 'No active buckets to export' });
        setExportingToTasks(false);
        return;
      }

      // Call API to export (pass all buckets for child lookup)
      const result = await exportToGoogleTasks(bucketsToExport, buckets, timeFrame);

      setExportMessage({
        type: 'success',
        text: `✅ Exported ${result.count} bucket${result.count > 1 ? 's' : ''} to Google Tasks!`
      });

    } catch (error) {
      console.error('Failed to export to Google Tasks:', error);
      setExportMessage({
        type: 'error',
        text: '❌ ' + (error.message || 'Failed to export. Please try again.')
      });
    } finally {
      setExportingToTasks(false);
    }
  };

  const totalPoints = (week) => Object.values(week ? weekPointsByColor : pointsByColor).reduce((acc, points) => acc + points, 0);
  const formatDate = (date) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const dayOfMonth = date.getDate();
    return `${month} ${dayOfMonth}`;
  };

  const resetSpreadSheet = async () => {
    console.log("resetSpreadSheet", process.env.REACT_APP_IS_ALL_LOCAL, process.env.REACT_APP_LOCAL_SERVER_HOST, process.env.REACT_APP_PROD_SERVER_HOST);
    try {
      const response = await fetch(
        (
          (process.env.REACT_APP_IS_ALL_LOCAL === "true")
            ? process.env.REACT_APP_LOCAL_SERVER_HOST
            : process.env.REACT_APP_PROD_SERVER_HOST) + '/sheets/defaulted',
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      console.log('Spreadsheet reset completed, reloading bucket data...');

      // Reload the spreadsheet data to display new buckets
      let bucketListData = await getSpreadsheetData();
      dispatch(setBucketList(bucketListData.values));
      if (bucketListData.calendarTodos) {
        dispatch(setCalendarTodos(bucketListData.calendarTodos));
      }
      console.log('Bucket data reloaded successfully after reset');

    } catch (error) {
      console.error('Error fetching cleared sheets:', error);
    }
  };

  async function fetchClearedSheets() {
    const requestInput = (process.env.REACT_APP_IS_ALL_LOCAL == "true" ?
      process.env.REACT_APP_LOCAL_SERVER_HOST :
      process.env.REACT_APP_PROD_SERVER_HOST) + '/sheets/cleared'

    console.log('request input', requestInput);

    try {
      await fetch(
        requestInput,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      console.log('Spreadsheet cleared, reloading bucket data...');

      // Reload the spreadsheet data to display new (empty) state
      let bucketListData = await getSpreadsheetData();
      dispatch(setBucketList(bucketListData.values));
      if (bucketListData.calendarTodos) {
        dispatch(setCalendarTodos(bucketListData.calendarTodos));
      }
      console.log('Bucket data reloaded successfully after clear');
    } catch (error) {
      console.error('Error fetching cleared sheets:', error);
    }
  };

  return (<>
    <nav style={{ flex: 1, backgroundColor: NAV_BACKGROUND_COLOR }}>
      <div className="logoAndTitle">
        <img src="/logo-five-buckets-organized.svg" alt="Life Buckets Logo" className="LifeBucketsLogo showOnMobile" style={{ width: "52px", height: "52px", marginLeft: "10px" }} />
        <h1 className="appTitle hideOnMobile" style={{ margin: "auto", marginLeft: "10px", color: "white" }}> Life Buckets</h1>
      </div>
      <div>
        <Tooltip title="current Bucket Date"><div className="date" style={{ borderColor: LUCKET_DETAILS_BACKGROUND_COLOR }}> {currentDate ? formatDate(new Date(currentDate)) : "Session Expired"}</div></Tooltip>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <LucketStepIcon step={step} setStep={dispatchSetStep} stepMenuOpenProp={stepMenuOpen} timeFrameMenuOpenProp={timeFrameMenuOpen} setStepMenuOpenProp={setStepMenuOpenProp} setTimeFrameMenuOpenProp={setTimeFrameMenuOpenProp} />
        <LucketTimeFrameIcon timeFrame={timeFrame} setTimeFrame={dispatchSetTimeFrame} timeFrameMenuOpenProp={timeFrameMenuOpen} stepMenuOpenProp={stepMenuOpen} setStepMenuOpenProp={setStepMenuOpenProp} setTimeFrameMenuOpenProp={setTimeFrameMenuOpenProp} />
      </div>
      {step == STEP_OPTION.Close && (<div>
        <LucketCloseOptionsIcon closeOption={closeOption} setCloseOption={dispatchSetCloseOption} closeOptionsMenuOpen={closeOptionsMenuOpen} setCloseOptionsMenuOpenProp={setCloseOptionsMenuOpenProp} stepMenuOpenProp={stepMenuOpen} />
      </div>)}
      {currentDate && currentDate != undefined && (
        <div className="hideInMobileNav">
          <LucketRelativePointsIcon luckets={buckets} currentDate={currentDate} />
        </div>
      )}
      {step == STEP_OPTION.Close && closeDay()}
      <span className="hideInMobileNav">
        {timeFrame == TIMEFRAME.Day ? pointsView() : timeFrame == TIMEFRAME.Week ? pointsView("week") : <div>
          {pointsView()}
          {pointsView("week")}
        </div>}
      </span>

      <div>
        <AddIcon
          id="addBucketIcon"
          style={{ width: "50px", height: "50px", margin: "auto" }}
          onClick={() => setAddBucketOpen(!addBucketOpen)}
        />
      </div>
      <div className="navbar-right">

        <div id="bucket-list-user-icon" className="user-icon" onClick={toggleDropdown}>
          {!imageError && user.picture ? (
            <img
              src={user.picture}
              alt="User Profile"
              onError={() => setImageError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="user-icon-fallback">
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
          )}

        </div>
        {dropdownVisible && (
          <div className="dropdown-menu">
            <div className="dropdown-header">
              <h2>{user.name}</h2>
            </div>

            <div className="dropdown-section">
              <button className="dropdown-btn" onClick={() => dispatch(toggleBucketViewIsOn())}>
                {bucketViewIsOn ? '🪣 Show List View' : '🪣 Show Grid View'}
              </button>
              <button className="dropdown-btn" onClick={handlePromptOpenAI}>
                🤖 Prompt OpenAI
              </button>
              <button className="dropdown-btn" onClick={() => setShowCalendar(!showCalendar)}>
                {showCalendar ? "📅 Hide Calendar" : "📅 Show Calendar"}
              </button>
              <button className="dropdown-btn" onClick={() => setShowSpreadsheetTable(!showSpreadsheetTable)}>
                {showSpreadsheetTable ? "📊 Hide Table" : "📊 Show Table"}
              </button>
              <button
                className="dropdown-btn"
                onClick={handleExportToGoogleTasks}
                disabled={exportingToTasks}
              >
                {exportingToTasks ? '⏳ Exporting...' : '📤 Export to Google Tasks'}
              </button>
            </div>

            {exportMessage && (
              <div className={`export-message ${exportMessage.type}`}>
                {exportMessage.text}
              </div>
            )}

            <div className="dropdown-divider"></div>

            <div className="dropdown-section">
              <button className="dropdown-btn dropdown-btn-logout" onClick={handleLogout}>
                🚪 Logout
              </button>
            </div>

            <div className="dropdown-divider"></div>

            <div className="dropdown-section danger-zone">
              <div className="danger-zone-header">
                ⚠️ Danger Zone
              </div>
              <button
                className="dropdown-btn dropdown-btn-danger"
                onClick={resetSpreadSheet}
              >
                🔄 Reset to Default Buckets
              </button>
              <button
                className="dropdown-btn dropdown-btn-danger"
                onClick={fetchClearedSheets}
              >
                🗑️ Clear All Data
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
    <div className="navBottom">
      {addBucketOpen && <AddBucket getSpreadsheetData={getSpreadsheetData} />}
    </div>
  </>

  );
};

export default NavBar;
